// components/GamePreferencesModal.tsx
import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonRadioGroup,
  IonRadio,
  IonListHeader,
  IonNote,
} from '@ionic/react';
import { UserGame, OwnershipStatus, TTRPGPreference } from '../types/games';

interface GamePreferencesModalProps {
  isOpen: boolean;
  game: UserGame | null;
  onDismiss: () => void;
  onSave: (preferences: {
    ownership_status?: OwnershipStatus;
    ttrpg_preference?: TTRPGPreference;
  }) => void;
}

export const GamePreferencesModal: React.FC<GamePreferencesModalProps> = ({
  isOpen,
  game,
  onDismiss,
  onSave,
}) => {
  const [ownershipStatus, setOwnershipStatus] = useState<OwnershipStatus>(null);
  const [ttrpgPreference, setTTRPGPreference] = useState<TTRPGPreference>(null);

  // Detect if this is an RPG game
  const isRPG = game?.game_type?.toLowerCase().includes('rpg') ||
                game?.name?.toLowerCase().includes('rpg') ||
                game?.name?.toLowerCase().includes('dungeons') ||
                game?.name?.toLowerCase().includes('pathfinder') ||
                game?.name?.toLowerCase().includes('call of cthulhu') ||
                false;

  useEffect(() => {
    if (game) {
      setOwnershipStatus(game.ownership_status || null);
      setTTRPGPreference(game.ttrpg_preference || null);
    }
  }, [game]);

  const handleSave = () => {
    onSave({
      ownership_status: ownershipStatus,
      ttrpg_preference: isRPG ? ttrpgPreference : null,
    });
  };

  const handleCancel = () => {
    // Reset to current values
    if (game) {
      setOwnershipStatus(game.ownership_status || null);
      setTTRPGPreference(game.ttrpg_preference || null);
    }
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleCancel}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Game Preferences</IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={handleCancel}>Cancel</IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} strong>
              Save
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {game && (
          <div>
            <IonList>
              <IonListHeader color="medium">
                <IonLabel>
                  <h2>{game.name}</h2>
                  <IonNote>Set your preferences for this game</IonNote>
                </IonLabel>
              </IonListHeader>

              {/* Ownership Status */}
              <IonListHeader color="medium">
                <IonLabel>Ownership Status</IonLabel>
              </IonListHeader>

              <IonRadioGroup
                value={ownershipStatus || 'none'}
                onIonChange={(e) => setOwnershipStatus(
                  e.detail.value === 'none' ? null : e.detail.value as OwnershipStatus
                )}
              >
                <IonItem
                  button
                  onClick={() => setOwnershipStatus('own')}
                >
                  <IonRadio slot="start" value="own" />
                  <IonLabel>
                    <h3>🎲 I Own This</h3>
                    <p>I have a physical or digital copy</p>
                  </IonLabel>
                </IonItem>

                <IonItem
                  button
                  onClick={() => setOwnershipStatus('want-to-try')}
                >
                  <IonRadio slot="start" value="want-to-try" />
                  <IonLabel>
                    <h3>🎯 Want to Try</h3>
                    <p>I'd like to play this game</p>
                  </IonLabel>
                </IonItem>

                <IonItem
                  button
                  onClick={() => setOwnershipStatus('played')}
                >
                  <IonRadio slot="start" value="played" />
                  <IonLabel>
                    <h3>⭐ Played Before</h3>
                    <p>I've played this but don't own it</p>
                  </IonLabel>
                </IonItem>

                <IonItem
                  button
                  onClick={() => setOwnershipStatus(null)}
                >
                  <IonRadio slot="start" value="none" />
                  <IonLabel>
                    <h3>No Preference</h3>
                    <p>Just tracking this game</p>
                  </IonLabel>
                </IonItem>
              </IonRadioGroup>

              {/* TTRPG Preferences - Only show for RPGs */}
              {isRPG && (
                <>
                  <IonListHeader color="medium">
                    <IonLabel>
                      <h2>TTRPG Role Preference</h2>
                      <IonNote>How do you prefer to play this RPG?</IonNote>
                    </IonLabel>
                  </IonListHeader>

                  <IonRadioGroup
                    value={ttrpgPreference || 'none'}
                    onIonChange={(e) => setTTRPGPreference(
                      e.detail.value === 'none' ? null : e.detail.value as TTRPGPreference
                    )}
                  >
                    <IonItem
                      button
                      onClick={() => setTTRPGPreference('run')}
                    >
                      <IonRadio slot="start" value="run" />
                      <IonLabel>
                        <h3>🎭 I Prefer to Run (GM/DM)</h3>
                        <p>I enjoy game mastering</p>
                      </IonLabel>
                    </IonItem>

                    <IonItem
                      button
                      onClick={() => setTTRPGPreference('play')}
                    >
                      <IonRadio slot="start" value="play" />
                      <IonLabel>
                        <h3>🎮 I Prefer to Play (Player)</h3>
                        <p>I enjoy being a player character</p>
                      </IonLabel>
                    </IonItem>

                    <IonItem
                      button
                      onClick={() => setTTRPGPreference('both')}
                    >
                      <IonRadio slot="start" value="both" />
                      <IonLabel>
                        <h3>🎯 Both GM and Player</h3>
                        <p>I'm happy with either role</p>
                      </IonLabel>
                    </IonItem>

                    <IonItem
                      button
                      onClick={() => setTTRPGPreference(null)}
                    >
                      <IonRadio slot="start" value="none" />
                      <IonLabel>
                        <h3>No Preference</h3>
                        <p>I haven't decided yet</p>
                      </IonLabel>
                    </IonItem>
                  </IonRadioGroup>
                </>
              )}
            </IonList>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};